import metadata from '@/data/weapon-effects.json';

const aliases = metadata.aliases as Record<string, number>;
const profiles = new Map(metadata.profiles.map(profile => [profile.id, profile]));
export interface WeaponEffectProfile {
  kind: string; chance_percent: number; cooldown_ms: number; duration_ms: number; magnitude: number;
}
export interface WeaponEffectEnhancement {
  weapon_effects?: WeaponEffectProfile[];
  elements?: {slot: number; element: string; source_item_id?: number}[];
}

/** Item IDs, never names/sprites: forged Luna aliases retain their base effect. */
export function weaponEffectLines(itemId: number, enhancement?: WeaponEffectEnhancement): string[] {
  if (!Number.isInteger(itemId) || itemId <= 0) return [];
  const canonical = aliases[String(itemId)] ?? itemId;
  if (metadata.direct_purchase_ids.includes(canonical)) return [];
  if (enhancement?.weapon_effects) return enhancement.weapon_effects.flatMap(formatEffect);
  const base = profiles.get(canonical);
  const gem = enhancement?.elements?.filter(e => e.slot > 0 && (e.source_item_id ?? 0) > 0)
    .sort((a,b) => b.slot - a.slot)[0];
  const override = metadata.gem_profiles.find(p => p.element.toLowerCase() === gem?.element.toLowerCase());
  if (override) return formatEffect({...override, chance_percent: base?.chance_percent ?? metadata.rules.gem_default_chance_percent});
  return [...(base ? [base] : []), ...metadata.secondary_profiles.filter(p => p.id === canonical)].flatMap(formatEffect);
}

function formatEffect(profile: WeaponEffectProfile): string[] {
  const chance = `${profile.chance_percent}% chance on hit`;
  const duration = `${profile.duration_ms / 1000}s`;
  let name: string, description: string;
  switch (profile.kind) {
    case 'slow':
      name = 'Slow';
      description = `${chance} to slow the target's attacks for ${duration}. Attacks take ${profile.magnitude}% longer.`;
      break;
    case 'confuse':
      name = 'Confusion';
      description = `${chance} to confuse the target, reducing accuracy by ${profile.magnitude}% for ${duration}.`;
      break;
    case 'sleep':
      name = 'Sleep';
      description = `${chance} to put the target to sleep for ${duration}. Sleeping enemies cannot move or attack. Damage wakes them.`;
      break;
    case 'double_hit':
      name = 'Double Strike';
      description = `${chance} to strike again for a full weapon attack. The extra strike can miss and cannot trigger weapon effects.`;
      break;
    case 'leech':
      name = 'Life Leech';
      description = `${chance} to restore HP equal to ${profile.magnitude}% of the damage dealt. Cannot exceed maximum HP.`;
      break;
    case 'deflect':
      name = 'Deflect';
      description = `${profile.chance_percent}% chance to block an incoming magic or ranged attack while equipped. Does not block melee attacks or damage over time.`;
      break;
    case 'burn': case 'poison': case 'bleed': case 'necrotic': {
      const names = { burn: 'Burn', poison: 'Poison', bleed: 'Bleed', necrotic: 'Necrotic Decay' };
      name = names[profile.kind as keyof typeof names];
      description = `${chance} to inflict ${name} for ${duration}, dealing ${profile.magnitude}% of the hit's damage every ${metadata.rules.dot_interval_ms / 1000}s. Does not stack.`;
      break;
    }
    default: return [];
  }
  return [`Weapon effect: ${name}`, description, `Effect cooldown: ${profile.cooldown_ms / 1000}s.`];
}
